import { handleActions } from 'redux-actions'
import { fromJS, Set, List } from 'immutable'

import * as Actions from 'actions/custom_tags'
import * as ShoppingCartActions from 'actions/shopping_cart'
import * as MediaReviewsActions from 'actions/media_reviews'
import * as AppActions from 'actions/app'

const createNewCustomTag = () => fromJS({
  name: '',
  description: '',
  queries: Set([]),
  newsCount: 0,
  percolationEnabled: false,
  archived: false,
  customTagGroupIds: List([])
})

export const initialState = fromJS({
  loading: false,
  showCustomTagDialog: false,
  showRemoveCustomTagDialog: false,
  showEmptyCustomTagDialog: false,
  uploadedPhoto: null,
  selectedCustomTag: createNewCustomTag(),
  lastUsedCustomTagIds: List([]),
  afterStructuredFormatExportAction: 'none',
  sortBy: 'name',
  sortingDirection: 'asc',
  groupedAssignableCustomTags: false,
  filteredGroupId: null,
  loadingCustomTagIds: Set([]),
  viewType: 'list',
  customTagsTab: 'all',
  menuCollapsedGroupIds: Set(),
  queryValidation: { loading: false, valid: null, error: false, requestId: null },
  menuGrouped: true
})

export default handleActions({
  [Actions.setSelectedCustomTag]: (state, { payload: customTag }) => state.set('selectedCustomTag', customTag),
  [Actions.deleteCustomTagQuery]: (state, { payload: query }) => state.updateIn(
    ['selectedCustomTag', 'queries'], queries => queries.delete(query)
  ),
  [Actions.clearCustomTagQueries]: state => state.updateIn(['selectedCustomTag', 'queries'], queries => queries.clear()),
  [Actions.validateCustomTagQueryStart]: (state, { payload: { requestId } }) => state.set(
    'queryValidation',
    fromJS({ loading: true, valid: null, error: false, requestId })
  ),
  [Actions.validateCustomTagQuerySuccess]: (state, { payload: { valid, query, previousQuery = null, requestId } }) => {
    if (!state.getIn(['queryValidation', 'loading']) || state.getIn(['queryValidation', 'requestId']) !== requestId) {
      return state
    }

    const nextState = state.set('queryValidation', fromJS({ loading: false, valid, error: false, requestId }))
    const normalizedQuery = query?.trim()

    if (valid !== true || !normalizedQuery) {
      return nextState
    }

    return nextState.updateIn(['selectedCustomTag', 'queries'], queries => (
      previousQuery === null
        ? queries.add(normalizedQuery)
        : queries.map(currentQuery => (currentQuery === previousQuery ? normalizedQuery : currentQuery))
    ))
  },
  [Actions.validateCustomTagQueryError]: (state, { payload: { requestId } }) => (
    state.getIn(['queryValidation', 'requestId']) === requestId
      ? state.set('queryValidation', fromJS({ loading: false, valid: false, error: true, requestId }))
      : state
  ),
  [Actions.clearCustomTagQueryValidation]: state => state.set('queryValidation', initialState.get('queryValidation')),
  [Actions.hideAssignCustomTagsToNewsDialog]: state => state
    .set('selectedCustomTag', initialState.get('selectedCustomTag'))
    .set('queryValidation', initialState.get('queryValidation'))
    .set('showCustomTagDialog', false),
  [Actions.setUploadedPhoto]: (state, { payload: data }) => state.set('uploadedPhoto', data),

  [Actions.showCustomTagDialog]: (state, { payload }) => {
    let selectedCustomTag = createNewCustomTag()

    if (payload) {
      selectedCustomTag = payload.update('queries', queries => Set(queries))
    }

    return state.merge({
      queryValidation: initialState.get('queryValidation'),
      showCustomTagDialog: true,
      uploadedPhoto: null,
      selectedCustomTag
    })
  },
  [Actions.hideCustomTagDialog]: state => state
    .set('showCustomTagDialog', false)
    .set('queryValidation', initialState.get('queryValidation')),
  [ShoppingCartActions.hideCustomTagDialog]: state => state
    .set('showCustomTagDialog', false)
    .set('queryValidation', initialState.get('queryValidation')),
  [MediaReviewsActions.hideCustomTagDialog]: state => state
    .set('showCustomTagDialog', false)
    .set('queryValidation', initialState.get('queryValidation')),

  [Actions.openCustomTagInShoppingCart]: state => state.set('loading', true),
  [ShoppingCartActions.fetchClusterForShoppingCartSuccess]: state => state.set('loading', false),

  [Actions.updateAfterStructuredFormatExportAction]: (state, { payload }) => state.merge({
    afterStructuredFormatExportAction: payload
  }),
  [Actions.hideEmptyCustomTagDialog]: state => state.set('showEmptyCustomTagDialog', false),
  [Actions.sortBy]: (state, { payload: { sortBy, direction } }) => state.merge({
    sortBy,
    sortingDirection: direction
  }),

  [Actions.showRemoveCustomTagDialog]: (state, { payload: customTag }) => state.merge({
    showRemoveCustomTagDialog: true,
    selectedCustomTag: customTag
  }),

  [Actions.showEmptyCustomTagDialog]: (state, { payload: customTag }) => state.merge({
    showEmptyCustomTagDialog: true,
    selectedCustomTag: customTag
  }),
  [Actions.hideRemoveCustomTagDialog]: state => state.set('showRemoveCustomTagDialog', false),

  [Actions.setFilteredGroupId]: (state, { payload: groupId }) => state.update('filteredGroupId', id => (id === groupId ? null : groupId)),
  [Actions.resetFilteredGroupId]: state => state.set('filteredGroupId', null),

  [Actions.setCustomTagsViewType]: (state, { payload }) => (
    state
      .set('sortBy', 'name')
      .set('sortingDirection', 'asc')
      .set('filteredGroupId', null)
      .set('viewType', payload)
  ),
  [Actions.changeCustomTagsTab]: (state, { payload }) => state.set('customTagsTab', payload),

  [Actions.toggleGroupedAssignableCustomTags]: state => state.update('groupedAssignableCustomTags', v => !v),
  [Actions.toggleMenuGroup]: (state, { payload: groupId }) => state.update('menuCollapsedGroupIds', ids => {
    const id = String(groupId)

    return ids.has(id) ? ids.delete(id) : ids.add(id)
  }),
  [Actions.collapseMenuGroups]: (state, { payload: groupIds }) => state.update(
    'menuCollapsedGroupIds',
    ids => ids.union(Set((groupIds || []).map(id => String(id))))
  ),
  [Actions.expandMenuGroups]: state => state.set('menuCollapsedGroupIds', Set()),
  [Actions.toggleMenuGrouped]: state => state.update('menuGrouped', grouped => !grouped),

  [Actions.assignCustomTagToNewsSuccess]: (state, { payload: { customTag } }) => (
    state
      .update('lastUsedCustomTagIds', l => (l || List([])).unshift(customTag.get('id')))
      .update('loadingCustomTagIds', l => (l || Set([])).remove(customTag.get('id')))
      .set('selectedCustomTag', initialState.get('selectedCustomTag'))
  ),
  [Actions.assignCustomTagToNewsStart]: (state, { payload: { customTag } }) => (
    state.update('loadingCustomTagIds', l => l.add(customTag.get('id')))
  ),
  [Actions.assignCustomTagToNewsError]: (state, { payload: { customTag } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTag.get('id')))
  ),

  [Actions.acceptCustomTagSuggestionsStart]: (state, { payload: { customTags } }) => (
    state.update('loadingCustomTagIds', ids => (ids || Set([])).union(customTags.map(tag => tag.get('id'))))
  ),
  [Actions.acceptCustomTagSuggestionsSuccess]: (state, { payload: { customTags } }) => (
    state
      .update('lastUsedCustomTagIds', ids => customTags.map(tag => tag.get('id')).concat(ids || List([])))
      .update('loadingCustomTagIds', ids => (ids || Set([])).subtract(customTags.map(tag => tag.get('id'))))
  ),
  [Actions.acceptCustomTagSuggestionsError]: (state, { payload: { customTags } }) => (
    state.update('loadingCustomTagIds', ids => (ids || Set([])).subtract(customTags.map(tag => tag.get('id'))))
  ),

  [Actions.removeCustomTagFromNewsStart]: (state, { payload: { customTag } }) => (
    state.update('loadingCustomTagIds', l => l.add(customTag.get('id')))
  ),
  [Actions.removeCustomTagFromNewsError]: (state, { payload: { customTag } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTag.get('id')))
  ),
  [Actions.removeCustomTagFromNewsSuccess]: (state, { payload: { customTag } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTag.get('id')))
  ),

  [Actions.assignCustomTagToGroupSuccess]: (state, { payload: { customTagId } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTagId))
  ),
  [Actions.assignCustomTagToGroupStart]: (state, { payload }) => {
    const customTagId = payload && typeof payload === 'object' ? payload.customTagId : payload

    return state.update('loadingCustomTagIds', ids => ids.add(customTagId))
  },
  [Actions.assignCustomTagToGroupError]: (state, { payload: { customTagId } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTagId))
  ),

  [Actions.removeCustomTagFromGroupStart]: (state, { payload }) => {
    const customTagId = payload && typeof payload === 'object' ? payload.customTagId : payload

    return state.update('loadingCustomTagIds', ids => ids.add(customTagId))
  },
  [Actions.removeCustomTagFromGroupError]: (state, { payload: { customTagId } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTagId))
  ),
  [Actions.removeCustomTagFromGroupSuccess]: (state, { payload: { customTagId } }) => (
    state.update('loadingCustomTagIds', l => l.remove(customTagId))
  ),

  [AppActions.resetState]: () => initialState
}, initialState)
